import { reactive } from 'vue';

import QuePasaChannelCreate from './components/QuePasaChannel.vue';
import QuePasaChannelSettingsTab from './routes/dashboard/settings/inbox/settingsPage/QuePasaChannelSettingsTab.vue';
import QuePasaChannelSyncTab from './routes/dashboard/settings/inbox/settingsPage/QuePasaChannelSyncTab.vue';
import { getStateColor } from './stateColors';

// Legada note: this entry is a rewrite of the premium index.js for the legada
// plugin system. The premium version imports dashboard/channels/registry,
// dashboard/extensions/registry and dashboard/plugin-system/dashboard-namespace
// — none of which exist here. Registration goes through
// window.chatwoot.channelRegistry (channel-extensibility) and PluginSystem
// (plugins/dashboard-api.ts) instead, and the settings tabs are ALWAYS
// registered via PluginSystem.registerTab because the legada channelRegistry
// has no settingsTabs consumer. The status badge / CSAT field extension-slot
// registrations were dropped (no ExtensionSlot in legada core).

const QUEPASA_CHANNEL_TYPE = 'Channel::Quepasa';
const QUEPASA_PROVIDER = 'quepasa_channel';
const STATE_CHANGED_SOCKET_EVENT = 'quepasa_channel.state_changed';
const STATE_CHANGED_DOM_EVENT = 'quepasa-channel:state-changed';

const getInboxChannelType = inbox => inbox?.channel_type || inbox?.channelType;

const getInboxProvider = inbox =>
  inbox?.provider ||
  inbox?.medium ||
  inbox?.additional_attributes?.provider ||
  inbox?.additionalAttributes?.provider ||
  inbox?.channel?.additional_attributes?.provider ||
  inbox?.channel?.additionalAttributes?.provider;

export const isQuePasaChannelInbox = inbox => {
  const channelType = getInboxChannelType(inbox);
  if (channelType === QUEPASA_CHANNEL_TYPE) return true;

  // Transitional: inboxes created by the old plugin version are Channel::Api +
  // provider quepasa_channel until the data migration promotes them.
  return (
    channelType === 'Channel::Api' &&
    getInboxProvider(inbox) === QUEPASA_PROVIDER
  );
};

// Live connection state per inbox id, fed by the websocket event emitted by
// the backend StateMonitorService on every state transition.
export const inboxStates = reactive({});

// Main channel tab + Sync (messages + contacts). The Webhooks tab is gone:
// inbound now arrives over the QuePasa Cable, matching premium, so there is
// no per-inbox webhook left to configure.
const SETTINGS_TABS = [
  {
    key: 'quepasa-channel-settings',
    // Label only - the core renders `tab.name` raw (no $t), and the `key`
    // above is what routing and automation target. Renaming the key would
    // break existing inboxes; renaming this string breaks nothing.
    name: 'QuePasa',
    component: QuePasaChannelSettingsTab,
  },
  {
    key: 'quepasa-channel-sync',
    name: 'Sync',
    component: QuePasaChannelSyncTab,
  },
];

export function install(app, { PluginSystem } = {}) {
  app.component('QuePasaChannelCreate', QuePasaChannelCreate);
  app.component('QuePasaChannelSettingsTab', QuePasaChannelSettingsTab);
  app.component('QuePasaChannelSyncTab', QuePasaChannelSyncTab);

  if (typeof window !== 'undefined') {
    window.chatwoot = window.chatwoot || {};
    window.chatwoot.plugins = window.chatwoot.plugins || {};
    window.chatwoot.plugins['channel-quepasa'] = {
      isQuePasaChannelInbox,
      inboxStates,
      getStateColor,
    };

    const registry = window.chatwoot.channelRegistry;
    if (registry?.register && !registry.has?.(QUEPASA_PROVIDER)) {
      registry.register({
        key: QUEPASA_PROVIDER,
        type: QUEPASA_CHANNEL_TYPE,
        title: 'QuePasa',
        description:
          'Direct QuePasa integration for standalone WhatsApp connections without N8N',
        icon: 'i-woot-quepasa',
        component: QuePasaChannelCreate,
        metadata: {
          plugin: 'quepasa-channel',
          provider: QUEPASA_PROVIDER,
          medium: QUEPASA_PROVIDER,
        },
      });
    }
  }

  if (!PluginSystem) return;

  PluginSystem.registerChannelType(QUEPASA_CHANNEL_TYPE, 'QUEPASA_CHANNEL');

  PluginSystem.registerProvider?.('whatsapp', {
    key: QUEPASA_PROVIDER,
    title: 'QuePasa',
    description:
      'Direct QuePasa integration for standalone WhatsApp connections without N8N',
    icon: 'i-woot-quepasa',
    component: QuePasaChannelCreate,
  });

  // Legada: always register the tabs here — the legada channelRegistry has no
  // settingsTabs support, so this is the only render path (no duplication).
  SETTINGS_TABS.forEach(tab => {
    PluginSystem.registerTab('Settings', {
      ...tab,
      condition: context => isQuePasaChannelInbox(context?.inbox),
    });
  });

  // Backend StateMonitorService broadcasts transitions to the account room;
  // fan the payload out to the reactive map and to a DOM event (settings tab
  // components listen for it).
  PluginSystem.registerSocketEvent?.(STATE_CHANGED_SOCKET_EVENT, payload => {
    const inboxId = payload?.inbox_id;
    if (!inboxId) return;
    inboxStates[inboxId] = payload.state || 'Unknown';
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(STATE_CHANGED_DOM_EVENT, { detail: payload })
      );
    }
  });
}

export default {
  install,
};
